import React, { useState, useEffect } from 'react';
import { t } from '../utils/i18n';
import CommonModal from './Modal';
import { useDatepicker } from '../hooks/useDatepicker';
import Pagination from './Pagination';
import AdminApplyLeave from './AdminApplyLeave';

const RANK_FULL_TITLES = {
  'S/Guud': 'Sareeye Guud (General)',
  'S/Guud:': 'Sareeye Guud (General)',
  'S/Gaas': 'Sareeye Gaas (Major General)',
  'S/Gaas:': 'Sareeye Gaas (Major General)',
  'G/Sare': 'Gaashaanle Sare (Colonel)',
  'G/Sare:': 'Gaashaanle Sare (Colonel)',
  'G/le': 'Gaashaanle (Major)',
  'G/le:': 'Gaashaanle (Major)',
  'Dhame': 'Dhame (Captain)',
  '2X/le': 'Laba Xiddigle (Lieutenant)',
  '2X/le:': 'Laba Xiddigle (Lieutenant)',
  'X/Le': 'Xiddigle (Second Lieutenant)',
  'X/Le:': 'Xiddigle (Second Lieutenant)',
  'K/S': 'Kormeere Sare (Senior Inspector)',
  'K/S:': 'Kormeere Sare (Senior Inspector)',
  'K/L': 'Kormeere Labaad (Inspector)',
  'K/L:': 'Kormeere Labaad (Inspector)',
  'K/K': 'Kormeere Keliya (Junior Inspector)',
  'K/K:': 'Kormeere Keliya (Junior Inspector)',
  'D/le': 'Dabaliye (Private / Soldier)',
  'D/le:': 'Dabaliye (Private / Soldier)'
};

const LeaveManagement = ({ api }) => {
  const [activeTab, setActiveTab] = useState('types');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [entitlementRules, setEntitlementRules] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [holidays, setHolidays] = useState([])
  const [locations, setLocations] = useState([]);
  const [armyPersonnel, setArmyPersonnel] = useState([]);
  const [armyTypes, setArmyTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [personnelFilter, setPersonnelFilter] = useState({ name: '', army_type_id: '', department_id: '', sub_department_id: '', army_id: '' });

  // Entitlements Tab Filter states
  const [entitlementFilterArmyType, setEntitlementFilterArmyType] = useState('');
  const [entitlementFilterDept, setEntitlementFilterDept] = useState('');
  const [entitlementFilterSubDept, setEntitlementFilterSubDept] = useState('');
  const [entitlementFilterOrg, setEntitlementFilterOrg] = useState('');
  const [entitlementFilterRank, setEntitlementFilterRank] = useState('');
  const [entitlementSearchTerm, setEntitlementSearchTerm] = useState('');

  const [loading, setLoading] = useState(false);
  const [ranks, setRanks] = useState([])
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    setCurrentPage(1);
    fetchData();
  }, [activeTab]);

  const handleDateChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };
  useDatepicker(handleDateChange);
  const fetchData = async () => {
    setLoading(true);
    try {
      const requests = [api.get('/leave_types'), api.get('/army_types'), api.get('/locations')];

      if (activeTab === 'rules') {
        requests.push(api.get('/leave_entitlement_rules'));
      } else if (activeTab === 'entitlements') {
        // fetch entitlements and personnel to allow creating one
        requests.push(api.get('/leave_entitlements'));
        requests.push(api.get('/army_personnel?per_page=10000'));
        requests.push(api.get('/departments'));
      } else if (activeTab === 'holidays') {
        requests.push(api.get('/holiday_calendars'));
      }

      const responses = await Promise.all(requests);
      setLeaveTypes(responses[0].data.leave_types || []);
      setArmyTypes(responses[1] || []);
      setLocations(responses[2] || []);

      if (activeTab === 'rules') {
        setEntitlementRules(responses[3].data.data || []);
        setRanks(responses[3].data.metadata.ranks || [])
      } else if (activeTab === 'entitlements') {
        setEntitlements(responses[3].data.leave_entitlements || []);
        setArmyPersonnel((responses[4] && (responses[4].data && responses[4].data.army_personnel)) || responses[4] || []);
        setDepartments(responses[5] || []);
      } else if (activeTab === 'holidays') {
        setHolidays(responses[3].data.holidays || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = getEndpoint();
      let payload = { [getPayloadKey()]: formData };

      // Handle date conversion for holidays
      if (activeTab === 'holidays') {
        const dateInput = document.querySelector('input[name="holiday_date"]');
        if (dateInput?.value) {
          // Convert MM/DD/YYYY to YYYY-MM-DD format
          const [month, day, year] = dateInput.value.split('/');
          const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          payload[getPayloadKey()].holiday_date = formattedDate;
        }
      }

      let result;
      if (editingItem) {
        result = await api.patch(`${endpoint}/${editingItem.id}`, payload);
      } else {
        result = await api.post(endpoint, payload);
      }

      if (result && result.response && result.response.ok) {
        fetchData();
        closeModal();
      } else {
        // Show server-side validation errors or raw response
        const data = result && result.data;
        if (data) {
          if (data.errors) {
            // ActiveRecord validation errors shape
            const messages = Object.values(data.errors).flat().join('\n');
            alert(messages);
          } else if (data.error) {
            if (Array.isArray(data.error)) {
              alert(data.error.join('\n'));
            } else if (typeof data.error === 'string') {
              alert(data.error);
            } else {
              alert(JSON.stringify(data.error));
            }
          } else if (data.__raw) {
            alert('Server error: ' + data.__raw.slice(0, 300));
          } else {
            alert('Failed to save.');
          }
        } else {
          alert('Failed to save.');
        }
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save leave entitlement: ' + (error.message || error));
    }
  };


  const handleDelete = async (id) => {
    if (confirm(t('confirm_delete', 'Are you sure you want to delete this record?'))) {
      try {
        await api.delete(`${getEndpoint()}/${id}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
  };

  const getEndpoint = () => {
    switch (activeTab) {
      case 'types': return '/leave_types';
      case 'rules': return '/leave_entitlement_rules';
      case 'entitlements': return '/leave_entitlements';
      case 'holidays': return '/holiday_calendars';
      default: return '/leave_types';
    }
  };

  const getPayloadKey = () => {
    switch (activeTab) {
      case 'types': return 'leave_type';
      case 'rules': return 'leave_entitlement_rule';
      case 'entitlements': return 'leave_entitlement';
      case 'holidays': return 'holiday';
      default: return 'leave_type';
    }
  };

  const filteredEntitlements = React.useMemo(() => {
    if (activeTab !== 'entitlements') return entitlements;

    return entitlements.filter(e => {
      const p = e.army_personnel || {};
      const fullP = armyPersonnel.find(ap => String(ap.id) === String(e.army_personnel_id || p.id)) || p;

      const pArmyTypeId = String(fullP.army_type_id || p.army_type_id || p.army_type?.id || '');
      const matchesArmyType = !entitlementFilterArmyType || pArmyTypeId === String(entitlementFilterArmyType);

      const pDeptId = String(fullP.department_id || p.department_id || p.department?.id || '');
      const matchesDept = !entitlementFilterDept || pDeptId === String(entitlementFilterDept);

      const pSubDeptId = String(fullP.sub_department_id || p.sub_department_id || p.sub_department?.id || '');
      const matchesSubDept = !entitlementFilterSubDept || pSubDeptId === String(entitlementFilterSubDept);

      const pOrg = String(fullP.organization || p.organization || '');
      const matchesOrg = !entitlementFilterOrg || pOrg === entitlementFilterOrg;

      const pRank = String(fullP.rank || p.rank || '');
      const matchesRank = !entitlementFilterRank || pRank === entitlementFilterRank;

      const pName = String(fullP.full_name || p.full_name || '');
      const pArmyId = String(fullP.user?.army_id || fullP.army_id || p.user?.army_id || '');
      const lType = String(e.leave_type?.name || e.leave_type || '');
      const matchesSearch = !entitlementSearchTerm ||
        pName.toLowerCase().includes(entitlementSearchTerm.toLowerCase()) ||
        pArmyId.toLowerCase().includes(entitlementSearchTerm.toLowerCase()) ||
        pRank.toLowerCase().includes(entitlementSearchTerm.toLowerCase()) ||
        lType.toLowerCase().includes(entitlementSearchTerm.toLowerCase());

      return matchesArmyType && matchesDept && matchesSubDept && matchesOrg && matchesRank && matchesSearch;
    });
  }, [entitlements, armyPersonnel, activeTab, entitlementFilterArmyType, entitlementFilterDept, entitlementFilterSubDept, entitlementFilterOrg, entitlementFilterRank, entitlementSearchTerm]);

  const getCurrentData = () => {
    switch (activeTab) {
      case 'types': return leaveTypes;
      case 'rules': return entitlementRules;
      case 'entitlements': return filteredEntitlements;
      case 'holidays': return holidays;
      default: return [];
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setFormData(getInitialFormData(item));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
    setPersonnelFilter({ name: '', army_type_id: '', department_id: '', sub_department_id: '', army_id: '' });
    const dateInput = document.querySelector('input[name="holiday_date"]');
    if (dateInput) dateInput.value = '';
  };

  const getInitialFormData = (item) => {
    if (!item) {
      switch (activeTab) {
        case 'types': return { name: '', code: '', max_days: '', description: '' };
        case 'rules': return { leave_type_id: '', army_type_id: '', rank: '', max_days: '' };
        case 'entitlements': return { army_personnel_id: '', leave_type_id: '', remaining_days: '', year: new Date().getFullYear() };
        case 'holidays': return { name: '', holiday_date: '', description: '', location_id: '', recurring: false };
        default: return {};
      }
    }
    return { ...item };
  };

  const currentData = getCurrentData();
  const paginatedData = currentData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(currentData.length / itemsPerPage);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'apply_leave':
        return <AdminApplyLeave api={api} />;
      case 'types':
        return renderLeaveTypesTable();
      case 'rules':
        return renderEntitlementRulesTable();
      case 'entitlements':
        return renderEntitlementsTable();
      case 'holidays':
        return renderHolidaysTable();
      default:
        return null;
    }
  };

  const renderLeaveTypesTable = () => (
    <table className="baseTable">
      <thead>
        <tr>
          <th>{t('name', 'Name')}</th>
          <th>{t('code', 'Code')}</th>
          <th>{t('max_days', 'Max Days')}</th>
          <th>{t('description', 'Description')}</th>
          <th>{t('actions', 'Actions')}</th>
        </tr>
      </thead>
      <tbody>
        {paginatedData.map((type) => (
          <tr key={type.id}>
            <td style={{ fontWeight: 600 }}>{type.name}</td>
            <td><code>{type.code}</code></td>
            <td>{type.max_days}</td>
            <td>{type.description || '-'}</td>
            <td>
              <div className="dFlex optBlock" style={{ gap: '6px' }}>
                <button
                  className="iconBtn iconBtnEdit"
                  onClick={() => openModal(type)}
                  title={t('edit', 'Edit')}
                  style={{ border: 'none', background: '#e3f2fd', color: '#1976d2', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500 }}
                >
                  ✏️ <span>{t('edit', 'Edit')}</span>
                </button>
                <button
                  className="iconBtn iconBtnDelete"
                  onClick={() => handleDelete(type.id)}
                  title={t('delete', 'Delete')}
                  style={{ border: 'none', background: '#ffebee', color: '#c62828', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500 }}
                >
                  🗑️ <span>{t('delete', 'Delete')}</span>
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderEntitlementRulesTable = () => (
    <table className="baseTable">
      <thead>
        <tr>
          <th>{t('leave_type', 'Leave Type')}</th>
          <th>{t('army_type', 'Force / Army Type')}</th>
          <th>{t('rank', 'Rank')}</th>
          <th>{t('max_entitlement_days', 'Max Entitlement (Days)')}</th>
          <th>{t('actions', 'Actions')}</th>
        </tr>
      </thead>
      <tbody>
        {paginatedData.map((rule) => (
          <tr key={rule.id}>
            <td style={{ fontWeight: 600 }}>{rule.leave_type?.name || 'N/A'}</td>
            <td>{rule.army_type?.name || 'All Forces'}</td>
            <td>
              <span title={RANK_FULL_TITLES[rule.rank] || rule.rank} style={{ cursor: 'help', borderBottom: '1px dotted #666', fontWeight: 500 }}>
                {rule.rank}
              </span>
            </td>
            <td style={{ fontWeight: 600, color: '#1a73e8' }}>{rule.max_days}</td>
            <td>
              <div className="dFlex optBlock" style={{ gap: '6px' }}>
                <button
                  className="iconBtn iconBtnEdit"
                  onClick={() => openModal(rule)}
                  title={t('edit', 'Edit')}
                  style={{ border: 'none', background: '#e3f2fd', color: '#1976d2', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500 }}
                >
                  ✏️ <span>{t('edit', 'Edit')}</span>
                </button>
                <button
                  className="iconBtn iconBtnDelete"
                  onClick={() => handleDelete(rule.id)}
                  title={t('delete', 'Delete')}
                  style={{ border: 'none', background: '#ffebee', color: '#c62828', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500 }}
                >
                  🗑️ <span>{t('delete', 'Delete')}</span>
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderEntitlementsTable = () => (
    <div>
      <div style={{ backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '14px', marginBottom: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
              {t('army_type', 'Force')}
            </label>
            <select
              value={entitlementFilterArmyType}
              onChange={(e) => {
                setEntitlementFilterArmyType(e.target.value);
                setEntitlementFilterDept('');
                setEntitlementFilterSubDept('');
              }}
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
            >
              <option value="">All Forces</option>
              {armyTypes.map(at => (
                <option key={at.id} value={at.id}>{at.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
              {t('department', 'Department')}
            </label>
            <select
              value={entitlementFilterDept}
              onChange={(e) => {
                setEntitlementFilterDept(e.target.value);
                setEntitlementFilterSubDept('');
              }}
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
            >
              <option value="">All Departments</option>
              {departments.filter(d => !d.parent_id).map(d => (
                <option key={d.id} value={d.id}>{d.somali_name || d.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
              {t('sub_department', 'Sub Department')}
            </label>
            <select
              value={entitlementFilterSubDept}
              onChange={(e) => setEntitlementFilterSubDept(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
            >
              <option value="">All Sub Departments</option>
              {departments.filter(d => d.parent_id).map(sd => (
                <option key={sd.id} value={sd.id}>{sd.somali_name || sd.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
              {t('organization', 'Organization')}
            </label>
            <select
              value={entitlementFilterOrg}
              onChange={(e) => setEntitlementFilterOrg(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
            >
              <option value="">All Organizations</option>
              {[...new Set(armyPersonnel.map(p => p.organization).filter(Boolean))].map(org => (
                <option key={org} value={org}>{org}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
              {t('rank', 'Rank')}
            </label>
            <select
              value={entitlementFilterRank}
              onChange={(e) => setEntitlementFilterRank(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
            >
              <option value="">All Ranks</option>
              {Object.keys(RANK_FULL_TITLES).map(rk => (
                <option key={rk} value={rk}>{rk}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
              {t('search', 'Search')}
            </label>
            <input
              type="text"
              placeholder={t('search_name_rank_id', 'Search by Name, Rank, Army ID...')}
              value={entitlementSearchTerm}
              onChange={(e) => setEntitlementSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
            />
          </div>
        </div>
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Showing <strong>{filteredEntitlements.length}</strong> of <strong>{entitlements.length}</strong> entitlements</span>
          {(entitlementFilterArmyType || entitlementFilterDept || entitlementFilterSubDept || entitlementFilterOrg || entitlementFilterRank || entitlementSearchTerm) && (
            <button
              onClick={() => {
                setEntitlementFilterArmyType('');
                setEntitlementFilterDept('');
                setEntitlementFilterSubDept('');
                setEntitlementFilterOrg('');
                setEntitlementFilterRank('');
                setEntitlementSearchTerm('');
              }}
              style={{ background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      <table className="baseTable">
        <thead>
          <tr>
            <th>{t('personnel', 'Personnel')}</th>
            <th>{t('force', 'Force / Army Type')}</th>
            <th>{t('rank', 'Rank')}</th>
            <th>{t('leave_type', 'Leave Type')}</th>
            <th>{t('remaining_days', 'Remaining Days')}</th>
            <th>{t('year', 'Year')}</th>
            <th>{t('actions', 'Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((entitlement) => {
            const p = entitlement.army_personnel || {};
            const fullP = armyPersonnel.find(ap => String(ap.id) === String(entitlement.army_personnel_id || p.id)) || p;
            const rankVal = fullP.rank || p.rank || 'N/A';
            const armyTypeName = fullP.army_type?.name || fullP.army_type || p.army_type?.name || p.army_type || 'N/A';

            return (
              <tr key={entitlement.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{fullP.full_name || p.full_name || 'N/A'}</div>
                  {(fullP.user?.army_id || p.user?.army_id) && (
                    <div style={{ fontSize: '11px', color: '#666' }}>ID: {fullP.user?.army_id || p.user?.army_id}</div>
                  )}
                </td>
                <td>{armyTypeName}</td>
                <td>
                  <span title={RANK_FULL_TITLES[rankVal] || rankVal} style={{ cursor: 'help', borderBottom: '1px dotted #666', fontWeight: 500 }}>
                    {rankVal}
                  </span>
                </td>
                <td>{entitlement.leave_type?.name || 'N/A'}</td>
                <td>
                  <span style={{ fontWeight: 600, color: entitlement.remaining_days > 5 ? '#2e7d32' : entitlement.remaining_days > 0 ? '#ed6c02' : '#d32f2f' }}>
                    {entitlement.remaining_days}
                  </span>
                </td>
                <td>{entitlement.year}</td>
                <td>
                  <div className="dFlex optBlock" style={{ gap: '6px' }}>
                    <button
                      className="iconBtn iconBtnEdit"
                      onClick={() => openModal(entitlement)}
                      title={t('edit', 'Edit')}
                      style={{ border: 'none', background: '#e3f2fd', color: '#1976d2', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500 }}
                    >
                      ✏️ <span>{t('edit', 'Edit')}</span>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderHolidaysTable = () => (
    <table className="baseTable">
      <thead>
        <tr>
          <th>{t('name', 'Name')}</th>
          <th>{t('date', 'Date')}</th>
          <th>{t('location', 'Location')}</th>
          <th>{t('recurring', 'Recurring')}</th>
          <th>{t('description', 'Description')}</th>
          <th>{t('actions', 'Actions')}</th>
        </tr>
      </thead>
      <tbody>
        {paginatedData.map((holiday) => (
          <tr key={holiday.id}>
            <td style={{ fontWeight: 600 }}>{holiday.name}</td>
            <td>{new Date(holiday.holiday_date).toLocaleDateString()}</td>
            <td>{holiday.location?.name || 'All Locations'}</td>
            <td>{holiday.recurring ? t('yes', 'Yes') : t('no', 'No')}</td>
            <td>{holiday.description || '-'}</td>
            <td>
              <div className="dFlex optBlock" style={{ gap: '6px' }}>
                <button
                  className="iconBtn iconBtnEdit"
                  onClick={() => openModal(holiday)}
                  title={t('edit', 'Edit')}
                  style={{ border: 'none', background: '#e3f2fd', color: '#1976d2', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500 }}
                >
                  ✏️ <span>{t('edit', 'Edit')}</span>
                </button>
                <button
                  className="iconBtn iconBtnDelete"
                  onClick={() => handleDelete(holiday.id)}
                  title={t('delete', 'Delete')}
                  style={{ border: 'none', background: '#ffebee', color: '#c62828', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500 }}
                >
                  🗑️ <span>{t('delete', 'Delete')}</span>
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderForm = () => {
    switch (activeTab) {
      case 'types':
        return (
          <>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('name')}</label>
              <input
                type="text"
                className="form-control"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('code')}</label>
              <input
                type="text"
                className="form-control"
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
              />
            </div>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('max_days')}</label>
              <input
                type="number"
                className="form-control"
                value={formData.max_days || ''}
                onChange={(e) => setFormData({ ...formData, max_days: e.target.value })}
                required
              />
            </div>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('description')}</label>
              <textarea
                className="form-control"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </>
        );
      case 'rules':
        return (
          <>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('leave_type')}</label>
              <select
                value={formData.leave_type_id || ''}
                onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
                required
              >
                <option value="">{t('select_leave_type')}</option>
                {leaveTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('army_type')}</label>
              <select
                value={formData.army_type_id || ''}
                onChange={(e) => setFormData({ ...formData, army_type_id: e.target.value })}
                required
              >
                <option value="">{t('select_army_type')}</option>
                {armyTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="formInpt mb_20">
              <label className="mb_8">{t('rank')}</label>
              <select
                value={formData.rank || ''}
                onChange={(e) => setFormData({ ...formData, rank: e.target.value })}
                required
              >
                <option value="">{t('select_rank')}</option>
                {ranks.map(rank => (
                  <option key={rank} value={rank}>{rank}</option>
                ))}
              </select>
            </div>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('max_days')}</label>
              <input
                type="number"
                className="form-control"
                value={formData.max_days || ''}
                onChange={(e) => setFormData({ ...formData, max_days: e.target.value })}
                required
              />
            </div>
          </>
        );
      case 'entitlements': {
        const subDepartments = Object.values(armyPersonnel.reduce((acc, p) => {
          if (p.sub_department?.id) acc[p.sub_department.id] = p.sub_department;
          return acc;
        }, {}));

        const filteredPersonnel = armyPersonnel.filter(p =>
          (!personnelFilter.army_type_id || String(p.army_type_id) === personnelFilter.army_type_id) &&
          (!personnelFilter.department_id || String(p.department_id) === personnelFilter.department_id) &&
          (!personnelFilter.sub_department_id || String(p.sub_department_id) === personnelFilter.sub_department_id) &&
          (!personnelFilter.army_id || (p.user?.army_id || '').toLowerCase().includes(personnelFilter.army_id.toLowerCase())) &&
          (!personnelFilter.name || [p.full_name, p.rank, p.department?.name, p.sub_department?.name].some(field => field?.toLowerCase().includes(personnelFilter.name.toLowerCase())))
        );
        return (
          <>
            <div className="formInpt mb_20">
              <label className="mb_8" style={{ textTransform: 'capitalize' }}>Filter Personnel</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                <select
                  className="form-control"
                  value={personnelFilter.army_type_id}
                  onChange={e => setPersonnelFilter(s => ({ ...s, army_type_id: e.target.value, department_id: '', sub_department_id: '' }))}
                >
                  <option value="">All Army Types</option>
                  {armyTypes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
                </select>

                <select
                  className="form-control"
                  value={personnelFilter.department_id}
                  onChange={e => setPersonnelFilter(s => ({ ...s, department_id: e.target.value, sub_department_id: '' }))}
                >
                  <option value="">All Departments</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>

                <select
                  className="form-control"
                  value={personnelFilter.sub_department_id}
                  onChange={e => setPersonnelFilter(s => ({ ...s, sub_department_id: e.target.value }))}
                >
                  <option value="">All Sub Departments</option>
                  {subDepartments.map(sd => <option key={sd.id} value={sd.id}>{sd.name}</option>)}
                </select>

                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Army ID"
                  value={personnelFilter.army_id}
                  onChange={e => setPersonnelFilter(s => ({ ...s, army_id: e.target.value }))}
                />

                <input
                  type="text"
                  className="form-control"
                  placeholder="Search name / rank / SP"
                  value={personnelFilter.name}
                  onChange={e => setPersonnelFilter(s => ({ ...s, name: e.target.value }))}
                />
              </div>
            </div>

            <div className="formInpt mb_20">
              <label className="mb_8" style={{ textTransform: 'capitalize' }}>Suggested Personnel</label>
              <div style={{ border: '1px solid #ddd', borderRadius: 6, maxHeight: 240, overflowY: 'auto' }}>
                {filteredPersonnel.length > 0 ? filteredPersonnel.map(person => {
                  const isSelected = String(formData.army_personnel_id) === String(person.id);
                  return (
                    <button
                      type="button"
                      key={person.id}
                      onClick={() => setFormData({ ...formData, army_personnel_id: person.id })}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        border: 'none',
                        backgroundColor: isSelected ? '#eaf4ff' : '#fff',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee'
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{person.full_name || 'Unnamed'}</div>
                      <div style={{ fontSize: 12, color: '#555' }}>
                        {person.user?.army_id ? `Army ID: ${person.user.army_id}` : ''}
                        {person.rank ? ` · ${person.rank}` : ''}
                        {person.department?.name ? ` · ${person.department.name}` : ''}
                        {person.sub_department?.name ? ` · ${person.sub_department.name}` : ''}
                      </div>
                    </button>
                  );
                }) : (
                  <div style={{ padding: '12px', color: '#777' }}>No personnel found</div>
                )}
              </div>
            </div>

            <div className="formInpt mb_20">
              <label className="mb_8">{t('leave_type')}</label>
              {(() => {
                const existingForPersonYear = entitlements.filter(e =>
                  formData.army_personnel_id &&
                  String(e.army_personnel_id) === String(formData.army_personnel_id) &&
                  formData.year &&
                  String(e.year) === String(formData.year)
                );
                const unavailableTypeIds = new Set(existingForPersonYear.map(e => String(e.leave_type_id)));

                return (
                  <select
                    className="form-control"
                    value={formData.leave_type_id || ''}
                    onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
                    required
                  >
                    <option value="">{t('select_leave_type')}</option>
                    {leaveTypes.map(type => {
                      const typeId = String(type.id);
                      const isCurrentType = editingItem && String(editingItem.leave_type_id) === typeId;
                      const isUnavailable = Boolean(formData.army_personnel_id && formData.year && !isCurrentType && unavailableTypeIds.has(typeId));

                      return (
                        <option key={type.id} value={type.id} disabled={isUnavailable}>
                          {type.name}
                          {isUnavailable ? ' (Already assigned)' : ''}
                        </option>
                      );
                    })}
                  </select>
                );
              })()}
            </div>

            <div className="formInpt mb_20">
              <label className="mb_8">{t('remaining_days')}</label>
              <input
                type="number"
                className="form-control"
                value={formData.remaining_days || ''}
                onChange={(e) => setFormData({ ...formData, remaining_days: e.target.value })}
                required
              />
            </div>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('year')}</label>
              <input
                type="number"
                className="form-control"
                value={formData.year || ''}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                required
              />
            </div>
          </>
        );
      }
      case 'holidays':
        return (
          <>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('name')}</label>
              <input
                type="text"
                className="form-control"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="formInpt mb_20 hasDatePicker">
              <label className="mb_8">{t('date')}</label>
              <input name="holiday_date" type="text" className="customDatepicker" placeholder="Select Date" defaultValue={formData.holiday_date} />
            </div>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('location')}</label>
              <select
                value={formData.location_id || ''}
                onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                required
              >
                <option value="">{t('select_location')}</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            </div>
            <div className="formInpt mb_20">
              <label className="mb_8">{t('description')}</label>
              <textarea
                className="form-control"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="mb_20">
              <label className="tccL fwMedium">
                <input
                  type="checkbox"
                  checked={formData.recurring || false}
                  onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                />
                <span className="checkmark"></span>
                {t('recurring_holiday')}
              </label>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="mainContent">
      <div className="mainContentCT dBlock">
        <div className="baseTableWrap">
          <div className="dFlex baseTableHd mb_16">
            <h3 className="txt20 fwMedium colorBlack">{t('leave_management', 'Leave Management')}</h3>
            <div className="dFlex">
              {activeTab !== 'apply_leave' && (
                <button onClick={() => openModal()} className="btn btn-primary">
                  {t('add_new', 'Add New')}
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="tabsContainer mb_16">
            <button
              className={`tab ${activeTab === 'types' ? 'active' : ''}`}
              onClick={() => setActiveTab('types')}
            >
              {t('leave_types', 'Leave Types')}
            </button>
            <button
              className={`tab ${activeTab === 'rules' ? 'active' : ''}`}
              onClick={() => setActiveTab('rules')}
            >
              {t('entitlement_rules', 'Entitlement Rules')}
            </button>
            <button
              className={`tab ${activeTab === 'entitlements' ? 'active' : ''}`}
              onClick={() => setActiveTab('entitlements')}
            >
              {t('entitlements', 'Leave Entitlements')}
            </button>
            <button
              className={`tab ${activeTab === 'holidays' ? 'active' : ''}`}
              onClick={() => setActiveTab('holidays')}
            >
              {t('holidays', 'Holidays')}
            </button>
            <button
              className={`tab ${activeTab === 'apply_leave' ? 'active' : ''}`}
              onClick={() => setActiveTab('apply_leave')}
            >
              {t('apply_leave', 'Apply Leave')}
            </button>
          </div>

          {loading && activeTab !== 'apply_leave' && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          <div className="baseTableContainer">
            {renderTabContent()}
          </div>

          {activeTab !== 'apply_leave' && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      <CommonModal
        isOpen={showModal}
        onClose={closeModal}
        title={
          activeTab === 'types' && editingItem ? t('edit_leave_type') :
            activeTab === 'types' && !editingItem ? t('create_leave_type') :
              activeTab === 'rules' && editingItem ? t('edit_entitlement_rule') :
                activeTab === 'rules' && !editingItem ? t('create_entitlement_rule') :
                  activeTab === 'entitlements' && editingItem ? t('edit_entitlement') :
                    activeTab === 'entitlements' && !editingItem ? t('create_entitlement') :
                      activeTab === 'holidays' && editingItem ? t('edit_holiday') :
                        activeTab === 'holidays' && !editingItem ? t('create_holiday') : ''
        }
        footerChildren={
          <>
            <div />
            <div className="dFlex" style={{ gap: '8px' }}>
              <button type="submit" form="leave-management-form" className="btn btn-primary">
                {editingItem ? t('update') : t('create')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeModal}>
                {t('close')}
              </button>
            </div>
          </>
        }
      >
        <form id="leave-management-form" onSubmit={handleSubmit}>
          {renderForm()}
        </form>
      </CommonModal>
    </div>
  );
};

export default LeaveManagement;
